import React, { useEffect } from 'react';
import { Grid, Button } from '@material-ui/core';
import { ModalWrapper } from 'components/Modals';
import DeleteIcon from '@material-ui/icons/Delete';
import './DeletePostModal.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from 'redux/store';
import { LoaderButton } from 'components/LoaderButton';
import { resetDeletePostSuccess, resetDeleteSubpostSuccess } from 'redux/actions/team';
import { deletePost, deleteSubpost } from 'redux/thunks';
import { Post } from 'redux/models/post';
import { UserIdName } from 'redux/models/userIdName';
import { Subpost } from 'redux/models/subpost';

interface DeleteModalProps {
	open: boolean;
	post?: Post | Subpost;
	isSubpost?: boolean;
	onCloseClicked: () => void;
	allowClickOutsideToClose?: boolean;
}

const DeletePostModal: React.FC<DeleteModalProps> = (props: DeleteModalProps) => {
	const deletingPost = useSelector((state: RootStore) => state.team.deletingPost);
	const deletePostSuccess = useSelector((state: RootStore) => state.team.deletePostSuccess);
	const deletingSubpost = useSelector((state: RootStore) => state.team.deletingSubpost);
	const deleteSubpostSuccess = useSelector((state: RootStore) => state.team.deleteSubpostSuccess);
	const dispatch = useDispatch();
	const { open, post, isSubpost = false, allowClickOutsideToClose, onCloseClicked } = props;

	const removePost = () => {
		if (post) {
			isSubpost ? dispatch(deleteSubpost(post as Subpost)) : dispatch(deletePost(post as Post));
		}
	};

	const createPostMsg = (recipients: UserIdName[]) => {
		let recipientList = buildUsersList(recipients);
		let actualPost = post as Post;
		return (
			<>
				Are you sure you want to delete this post to <span className='delete-post__users'>{recipientList}</span>?
				{actualPost!.subpostCount > 0 && <span> This post and all subposts on this post will be deleted.</span>}
				&nbsp;This action cannot be undone.
			</>
		);
	};

	const createSubpostMsg = (recipients: UserIdName[]) => {
		let recipientList = buildUsersList(recipients);
		return (
			<>
				Are you sure you want to delete this subpost to <span className='delete-post__users'>{recipientList}</span>? This action cannot be undone.
			</>
		);
	};

	const buildUsersList = (users: UserIdName[]) => {
		let userString = '';

		users.forEach((user, index) => {
			if (index + 1 === users.length) {
				userString += `${user.name}`;
			} else {
				userString += `${user.name},`;
			}
		});

		return userString;
	};

	useEffect(() => {
		if (deletePostSuccess) {
			dispatch(resetDeletePostSuccess());
			onCloseClicked();
		} else if (deleteSubpostSuccess) {
			dispatch(resetDeleteSubpostSuccess());
			onCloseClicked();
		}
	}, [deletePostSuccess, deleteSubpostSuccess, dispatch, onCloseClicked]);

	return (
		<ModalWrapper open={open} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
			<Grid container direction='column' alignItems='center' className='confirmation-container'>
				<h3 className='toa-title'>Delete {isSubpost ? 'Subpost' : 'Post'}</h3>
				<div className='confirmation-text'>{post && isSubpost === false ? createPostMsg(post.users) : post && createSubpostMsg(post.users)}</div>
				<div className='confirmation-button-container'>
					<Button variant='contained' color='primary' className='confirmation-cancel-button' onClick={onCloseClicked}>
						cancel
					</Button>
					<LoaderButton
						type='button'
						loading={deletingPost || deletingSubpost}
						onHandleClick={removePost}
						variant='outlined'
						color='none'
						className='confirmation-delete-button'
						startIcon={<DeleteIcon />}
					>
						Delete {isSubpost ? 'Subpost' : 'Post'}
					</LoaderButton>
				</div>
				{/* </Grid> */}
			</Grid>
		</ModalWrapper>
	);
};

export default DeletePostModal;
